/**
 * Arithmetic Evaluation Utility
 * Detects a trailing "=" after a simple arithmetic expression (digits,
 * + - * / . ( ) and thousands-separator commas) typed into a text field and
 * inserts the computed result right after the "=", e.g. "1,000+1=" ->
 * "1,000+1=1,001". The original expression and "=" are left untouched; the
 * inserted result is itself comma-grouped.
 */

// Characters allowed to make up an arithmetic expression (comma is a
// thousands separator here, not a decimal point or argument separator)
const EXPRESSION_PATTERN = /^[0-9+\-*/.,() \t]+$/;
// Same set minus comma, used while scanning backward one character at a
// time so a comma can be special-cased (see isThousandsSeparatorComma)
const NON_COMMA_ARITHMETIC_CHAR = /[0-9+\-*/.() \t]/;
const DIGIT = /[0-9]/;

// A comma only counts as part of the number (thousands separator) when it
// sits directly between two digits, e.g. "33,555". A comma followed by a
// space (or anything else) is treated as punctuation/a delimiter between
// separate calculations on the same line, e.g. "...=33,555, 333+22="
const isThousandsSeparatorComma = (value, commaIndex) => {
  const before = value[commaIndex - 1];
  const after = value[commaIndex + 1];
  return DIGIT.test(before) && DIGIT.test(after);
};

const formatResult = (result) => {
  return result.toLocaleString('en-US', { maximumFractionDigits: 6 });
};

const evaluateExpression = (expr) => {
  if (!EXPRESSION_PATTERN.test(expr)) {
    return null;
  }

  // Commas are thousands separators, not valid JS syntax - strip them
  const sanitized = expr.replace(/,/g, '');

  let result;
  try {
    // eslint-disable-next-line no-new-func
    result = Function(`"use strict"; return (${sanitized});`)();
  } catch (e) {
    return null;
  }

  if (typeof result !== 'number' || !Number.isFinite(result)) {
    return null;
  }

  return result;
};

/**
 * Checks if a trailing "=" should trigger arithmetic evaluation and returns the result
 * @param {string} value - Current field value
 * @param {number} cursorPos - Current cursor position
 * @param {string} prevValue - Previous field value (before this change)
 * @returns {object|null} - { newValue, cursorPos } if evaluation happened, null otherwise
 */
export const checkArithmeticEval = (value, cursorPos, prevValue = '') => {
  // Only trigger right after the user types a single '=' character
  if (prevValue) {
    const lengthDiff = value.length - prevValue.length;
    if (lengthDiff !== 1) {
      return null;
    }
    if (value[cursorPos - 1] !== '=') {
      return null;
    }
  } else {
    if (value[cursorPos - 1] !== '=') {
      return null;
    }
  }

  // Scan backward from just before the '=' to find the arithmetic expression,
  // stopping at a prior '=' or other boundary so a second calculation on the
  // same line (e.g. "...=33,555, 333+22=") evaluates independently
  let start = cursorPos - 1;
  while (start > 0) {
    const ch = value[start - 1];
    if (ch === ',') {
      if (!isThousandsSeparatorComma(value, start - 1)) {
        break;
      }
    } else if (!NON_COMMA_ARITHMETIC_CHAR.test(ch)) {
      break;
    }
    start--;
  }

  // Trim leading/trailing whitespace from the matched span (only used to
  // validate/evaluate the expression, not to alter what's kept in the field)
  const raw = value.slice(start, cursorPos - 1);
  const leading = raw.match(/^[ \t]*/)[0].length;
  const trailing = raw.match(/[ \t]*$/)[0].length;
  const expr = value.slice(start + leading, cursorPos - 1 - trailing);

  if (!expr || !/\d/.test(expr) || !/[+\-*/]/.test(expr)) {
    return null;
  }

  const result = evaluateExpression(expr);
  if (result === null) {
    return null;
  }

  // Keep the expression and "=" as typed, insert the answer right after it
  const formatted = formatResult(result);
  const newValue = value.slice(0, cursorPos) + formatted + value.slice(cursorPos);

  return {
    newValue,
    cursorPos: cursorPos + formatted.length
  };
};

/**
 * Handles arithmetic evaluation on a textarea/input
 * Call this in onChange handler after setting the value
 * @param {HTMLElement} element - The textarea/input element
 * @param {string} value - The new value (after user input)
 * @param {function} setValue - Function to update the value state
 * @param {string} prevValue - Optional previous value for better detection
 */
export const handleArithmeticInput = (element, value, setValue, prevValue = '') => {
  const result = checkArithmeticEval(value, element.selectionStart, prevValue);

  if (result) {
    setValue(result.newValue);

    // Set cursor position after React re-renders
    setTimeout(() => {
      element.selectionStart = result.cursorPos;
      element.selectionEnd = result.cursorPos;
      element.focus();
    }, 0);

    return true;
  }

  return false;
};

export default {
  checkArithmeticEval,
  handleArithmeticInput
};
